import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import * as productApi from '../../shared/api/products.js'
import type { CategoryCountsItem } from '../../shared/api/products.js'
import * as menuApi from '../../shared/api/menus.js'
import * as categoryApi from '../../shared/api/categories.js'
import type {
  CatalogStatus,
  Category,
  Menu,
  PaginationMeta,
  Product,
  ProductVariant,
} from '../../shared/types/models.js'
import { canManageCatalog } from '../../shared/utils/permissions.js'
import { SkeletonTableRow } from '../../shared/ui/Skeleton.js'
import {
  availableLifecycleActions,
  formatPrice,
  formatPriceRange,
  type LifecycleAction,
} from '../../shared/utils/catalog.js'
import { ApiError } from '../../shared/types/api.js'
import { getOrganization } from '../../shared/api/organization.js'
import ProductForm from './components/ProductForm.js'
import EmptyState from '../menu/components/EmptyState.js'
import StatusDot from '../../shared/ui/StatusDot.js'
import SidePanel from '../../shared/ui/SidePanel.js'
import IconButton from '../../shared/ui/IconButton.js'
import { ArchiveIcon, GearIcon, PauseCircleIcon, PlayCircleIcon } from '../../shared/ui/icons.js'

type ListStatus = 'loading' | 'ready' | 'error' | 'forbidden'

const PAGE_SIZE = 20

const ACTION_LABEL: Record<LifecycleAction, string> = {
  suspend: 'products.suspend',
  activate: 'products.activate',
  archive: 'products.archive',
}

const STATUS_LABEL: Record<CatalogStatus, string> = {
  ACTIVE: 'products.statusActive',
  SUSPENDED: 'products.statusSuspended',
  ARCHIVED: 'products.statusArchived',
}

function productPriceLabel(product: Product): string {
  const list = product.variants?.length ? product.variants : []
  const visible = list.filter(v => v.status !== 'ARCHIVED')
  const source = visible.length > 0 ? visible : list
  if (source.length === 0) {
    return product.baseVariant ? formatPrice(product.baseVariant.price) : '—'
  }
  const prices = source.map(v => v.price)
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  return min === max ? formatPrice(min) : formatPriceRange(min, max)
}

export default function ProductsPage() {
  const { t } = useTranslation()

  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const role = useOrgStore(s => s.activeOrganization)?.role
  const canManage = canManageCatalog(role)

  const [menus, setMenus] = useState<Menu[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [selectableCategories, setSelectableCategories] = useState<Category[]>([])
  const [categoriesError, setCategoriesError] = useState(false)

  const [menuFilter, setMenuFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [lastCategoryId, setLastCategoryId] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const [products, setProducts] = useState<Product[]>([])
  const [meta, setMeta] = useState<PaginationMeta>({ total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 })
  const [status, setStatus] = useState<ListStatus>('loading')
  const [actionError, setActionError] = useState('')
  const [success, setSuccess] = useState('')
  const [pricePrecisionMode, setPricePrecisionMode] = useState('INTEGER_ONLY')

  // Server-side category counts (reflect total scope, not just current page)
  const [serverCategoryCounts, setServerCategoryCounts] = useState<CategoryCountsItem[]>([])

  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    ;(async () => {
      try {
        const counts = await productApi.getProductCategoryCounts(orgId, {
          search: search || undefined,
          status: (statusFilter || undefined) as CatalogStatus | undefined,
        })
        if (!cancelled) setServerCategoryCounts(counts)
      } catch {
        // Non-critical - fall back to page-level counts
      }
    })()
    return () => { cancelled = true }
  }, [orgId, search, statusFilter])

  const [formMode, setFormMode] = useState<{ type: 'create' } | { type: 'edit'; product: Product } | null>(null)
  const [dirty, setDirty] = useState(false)
  const [pending, setPending] = useState<{ id: string; action: LifecycleAction } | null>(null)

  // Menus + their categories. Products belong to categories, so the category
  // filter and the create/edit form both need the org's categories.
  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    ;(async () => {
      try {
        const menusRes = await menuApi.getMenus(orgId)
        if (cancelled) return
        setMenus(menusRes.data)
        // Full list: category names for rows and the read filter. Selectable
        // list: the backend `writable` filter, so archived menus/categories can
        // never be chosen when creating/editing a product.
        const [allResults, writableResults] = await Promise.all([
          Promise.all(menusRes.data.map(menu => categoryApi.getCategories(orgId, menu.id))),
          Promise.all(
            menusRes.data
              .filter(menu => menu.status !== 'ARCHIVED')
              .map(menu => categoryApi.getCategories(orgId, menu.id, { writable: true })),
          ),
        ])
        if (cancelled) return
        setCategories(allResults.flatMap(r => r.data))
        setSelectableCategories(writableResults.flatMap(r => r.data))
        setCategoriesError(false)
      } catch {
        if (!cancelled) setCategoriesError(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgId])

  // Load org's price precision mode
  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    ;(async () => {
      try {
        const org = await getOrganization(orgId)
        if (!cancelled) setPricePrecisionMode(org.pricePrecisionMode || 'INTEGER_ONLY')
      } catch {
        if (!cancelled) setPricePrecisionMode('INTEGER_ONLY')
      }
    })()
    return () => { cancelled = true }
  }, [orgId])

  // Debounced server-side search.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const load = useCallback(async () => {
    if (!orgId) return
    setStatus('loading')
    setActionError('')
    try {
      const res = await productApi.getProducts(orgId, {
        categoryId: categoryFilter || undefined,
        search: search || undefined,
        status: (statusFilter || undefined) as CatalogStatus | undefined,
        page,
        pageSize: PAGE_SIZE,
      })
      setProducts(res.data)
      setMeta(res.meta)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error')
    }
  }, [orgId, categoryFilter, search, statusFilter, page])

  useEffect(() => {
    void load()
  }, [load])

  const categoryOptions = useMemo(
    () => {
      const activeMenuIds = new Set(menus.filter(m => m.status !== 'ARCHIVED').map(m => m.id))
      const valid = categories.filter(c => activeMenuIds.has(c.menuId) && c.status !== 'ARCHIVED')
      return menuFilter ? valid.filter(c => c.menuId === menuFilter) : valid
    },
    [categories, menus, menuFilter],
  )

  // Category counts from server (reflecting full filter scope, not just page)
  // Filtered to only show counts for visible categories (matching menu filter)
  const categoryCounts = useMemo(() => {
    const visibleCategoryIds = new Set(categoryOptions.map(c => c.id))
    const counts: Record<string, number> = {}
    for (const item of serverCategoryCounts) {
      if (visibleCategoryIds.has(item.categoryId)) {
        counts[item.categoryId] = item.count
      }
    }
    return counts
  }, [serverCategoryCounts, categoryOptions])

  const totalCount = useMemo(
    () => Object.values(categoryCounts).reduce((a, b) => a + b, 0),
    [categoryCounts],
  )

  // Only categories that can receive a product (active chain), per the backend.
  const formCategoryOptions = useMemo(
    () =>
      menuFilter
        ? selectableCategories.filter(c => c.menuId === menuFilter)
        : selectableCategories,
    [selectableCategories, menuFilter],
  )
  const hasSelectableCategories = selectableCategories.length > 0

  const categoryNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const category of categories) map.set(category.id, category.name)
    return map
  }, [categories])

  function handleMenuChange(value: string) {
    setMenuFilter(value)
    const activeMenuIds = new Set(menus.filter(m => m.status !== 'ARCHIVED').map(m => m.id))
    const validCategories = categories.filter(c => activeMenuIds.has(c.menuId) && c.status !== 'ARCHIVED')
    const nextOptions = value ? validCategories.filter(c => c.menuId === value) : validCategories
    if (categoryFilter && !nextOptions.some(c => c.id === categoryFilter)) {
      setCategoryFilter('')
    }
    setPage(1)
  }

  function handleCategoryChange(value: string) {
    setCategoryFilter(value)
    setPage(1)
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value)
    setPage(1)
  }

  function resetFilters() {
    setMenuFilter('')
    setCategoryFilter('')
    setStatusFilter('')
    setSearchInput('')
    setSearch('')
    setPage(1)
  }

  function openCreate() {
    setDirty(false)
    setFormMode({ type: 'create' })
  }

  function openEdit(product: Product) {
    setDirty(false)
    setFormMode({ type: 'edit', product })
  }

  function closeForm() {
    setFormMode(null)
    setDirty(false)
  }

  function requestCloseForm() {
    if (dirty && !window.confirm(t('common.unsavedChanges'))) return
    closeForm()
  }

  function handleSaved(product: Product, isEdit: boolean) {
    if (!isEdit) setLastCategoryId(product.categoryId)
    setProducts(prev => {
      const exists = prev.some(p => p.id === product.id)
      return exists ? prev.map(p => (p.id === product.id ? product : p)) : [product, ...prev]
    })
    closeForm()
    setActionError('')
    setSuccess(isEdit ? t('products.saveSuccess') : t('products.createdSuccess'))
  }

  function handleVariantsChanged(productId: string, variants: ProductVariant[]) {
    setProducts(prev =>
      prev.map(p => {
        if (p.id !== productId) return p
        const base = variants.find(v => v.isDefault) ?? null
        return {
          ...p,
          variants,
          baseVariant: base
            ? { id: base.id, name: base.name, price: base.price, oldPrice: base.oldPrice, sku: base.sku }
            : null,
        }
      }),
    )
  }

  async function handleLifecycle(product: Product, action: LifecycleAction) {
    if (!orgId) return
    if (action === 'archive') {
      const ok = window.confirm(t('products.archiveConfirm', { name: product.name }))
      if (!ok) return
    }
    setActionError('')
    setSuccess('')
    setPending({ id: product.id, action })
    try {
      const updated =
        action === 'suspend'
          ? await productApi.suspendProduct(orgId, product.id)
          : action === 'activate'
            ? await productApi.activateProduct(orgId, product.id)
            : await productApi.archiveProduct(orgId, product.id)
      setProducts(prev => prev.map(p => (p.id === updated.id ? updated : p)))
    } catch {
      setActionError(t('products.lifecycleError'))
    } finally {
      setPending(null)
    }
  }

  if (!orgId) return null

  const hasActiveFilters = Boolean(search || categoryFilter || statusFilter)

  if (status === 'loading' && products.length === 0 && !formMode) {
    return (
      <section className="page-section">
        <h2>{t('app.products')}</h2>
        <div className="page-loading">
          {Array.from({ length: 5 }, (_, i) => <SkeletonTableRow key={i} />)}
        </div>
      </section>
    )
  }

  if (status === 'error' || status === 'forbidden') {
    return (
      <section className="page-section">
        <h2>{t('app.products')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">
            {status === 'forbidden' ? t('products.forbidden') : t('products.loadErrorList')}
          </div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h2>{t('app.products')}</h2>
          <p className="page-section-description">{t('products.listSubtitle')}</p>
        </div>
        {canManage && !formMode && hasSelectableCategories && (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            {t('products.add')}
          </button>
        )}
      </div>

      <div className="product-filters">
        <div className="form-field">
          <label htmlFor="productMenuFilter">{t('products.menu')}</label>
          <select
            id="productMenuFilter"
            value={menuFilter}
            onChange={e => handleMenuChange(e.target.value)}
            disabled={menus.length === 0}
          >
            <option value="">{t('products.allMenus')}</option>
            {menus.filter(m => m.status !== 'ARCHIVED').map(menu => (
              <option key={menu.id} value={menu.id}>
                {menu.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="productCategoryFilter">{t('products.category')}</label>
          <select
            id="productCategoryFilter"
            value={categoryFilter}
            onChange={e => handleCategoryChange(e.target.value)}
          >
            <option value="">{t('products.allCategories')} ({totalCount})</option>
            {categoryOptions.map(category => (
              <option key={category.id} value={category.id}>
                {category.name} ({categoryCounts[category.id] ?? 0})
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="productStatusFilter">{t('products.statusFilter')}</label>
          <select
            id="productStatusFilter"
            value={statusFilter}
            onChange={e => handleStatusChange(e.target.value)}
          >
            <option value="">{t('products.statusAll')}</option>
            <option value="ACTIVE">{t('products.statusActive')}</option>
            <option value="SUSPENDED">{t('products.statusSuspended')}</option>
            <option value="ARCHIVED">{t('products.statusArchived')}</option>
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="productSearch">{t('products.searchPlaceholder')}</label>
          <input
            id="productSearch"
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            maxLength={100}
            placeholder={t('products.searchPlaceholder')}
            autoComplete="off"
          />
        </div>
      </div>

      {categoriesError && <div className="form-error-banner">{t('products.categoriesLoadError')}</div>}
      {success && (
        <div className="form-success-banner" role="status">
          {success}
        </div>
      )}
      {actionError && <div className="form-error-banner">{actionError}</div>}

      {products.length === 0 ? (
        hasActiveFilters ? (
          <EmptyState
            title={t('products.noResults')}
            hint={t('products.noResultsHint')}
            actionLabel={t('products.resetFilters')}
            onAction={resetFilters}
          />
        ) : (
          <EmptyState
            title={t('products.empty')}
            hint={t('products.emptyHint')}
            actionLabel={
              canManage && hasSelectableCategories && !formMode ? t('products.addFirst') : undefined
            }
            onAction={canManage && hasSelectableCategories && !formMode ? openCreate : undefined}
          />
        )
      ) : (
        <>
          <ul className="product-list">
            {products.map(product => {
              const actions = availableLifecycleActions(product.status)
              const rowPending = pending?.id === product.id
              const categoryName = categoryNames.get(product.categoryId) ?? t('products.unknownCategory')
              const statusLabel = t(STATUS_LABEL[product.status])
              const variantHint = product.variants.length > 1
                ? t('products.variantCount', { count: product.variants.length })
                : undefined
              const editable = canManage && product.status !== 'ARCHIVED'
              const thumbInner = product.primaryImageUrl
                ? <img src={product.primaryImageUrl} alt="" className="product-thumb-img" />
                : <span className="product-thumb-placeholder" />
              return (
                <li key={product.id} className={`product-row product-row--${product.status.toLowerCase()}`} aria-busy={rowPending}>
                  {editable ? (
                    <button
                      type="button"
                      className="product-thumb product-thumb--link"
                      onClick={() => openEdit(product)}
                      aria-label={t('products.editAria', { name: product.name })}
                    >
                      {thumbInner}
                    </button>
                  ) : (
                    <span className="product-thumb">{thumbInner}</span>
                  )}

                  <StatusDot status={product.status} label={statusLabel} />

                  <span className="product-name" title={variantHint ? `${product.name} · ${variantHint}` : product.name}>
                    {editable ? (
                      <button type="button" className="product-title-link" onClick={() => openEdit(product)}>
                        {product.name}
                      </button>
                    ) : (
                      product.name
                    )}
                  </span>

                  <span className="product-row-meta">
                    <span className="product-nutrition">
                      {product.weight != null ? t('products.weightShort', { value: product.weight }) : '—'}
                    </span>
                    <span className="product-category" title={categoryName}>{categoryName}</span>
                  </span>

                  <span className="product-row-price">{productPriceLabel(product)}</span>

                  {canManage && product.status !== 'ARCHIVED' && (
                    <div className="product-row-actions">
                      <IconButton
                        label={t('products.edit')}
                        disabled={rowPending}
                        onClick={() => openEdit(product)}
                      >
                        <GearIcon />
                      </IconButton>
                      {actions.map(action => (
                        <IconButton
                          key={action}
                          label={t(ACTION_LABEL[action])}
                          disabled={rowPending}
                          danger={action === 'archive'}
                          onClick={() => void handleLifecycle(product, action)}
                        >
                          {rowPending && pending?.action === action ? (
                            <span className="icon-spinner" aria-label={t('products.processing')} />
                          ) : action === 'archive' ? (
                            <ArchiveIcon />
                          ) : action === 'suspend' ? (
                            <PauseCircleIcon />
                          ) : (
                            <PlayCircleIcon />
                          )}
                        </IconButton>
                      ))}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>

          {meta.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={meta.page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                {t('products.previous')}
              </button>
              <span className="pagination-label">
                {t('products.pageOf', { page: meta.page, total: meta.totalPages })}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                {t('products.next')}
              </button>
            </div>
          )}
        </>
      )}

      <SidePanel
        open={formMode !== null}
        onClose={requestCloseForm}
        title={formMode?.type === 'edit' ? t('products.editTitle') : t('products.createTitle')}
      >
        {formMode && (
          <div onInput={() => setDirty(true)} onChange={() => setDirty(true)}>
            <ProductForm
              organizationId={orgId}
              categories={formCategoryOptions.length > 0 ? formCategoryOptions : selectableCategories}
              initial={formMode.type === 'edit' ? formMode.product : (lastCategoryId ? { categoryId: lastCategoryId } as any : null)}
              disabled={false}
              pricePrecisionMode={pricePrecisionMode}
              hideHeading
              onSaved={handleSaved}
              onCancel={requestCloseForm}
              onVariantsChanged={handleVariantsChanged}
            />
          </div>
        )}
      </SidePanel>
    </section>
  )
}